// src/auth/RoleGuard.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { getLoginRequest } from "../msal";

const SS = sessionStorage;
const K = { role: "tb_role", roleRaw: "tb_role_raw", roleSelected: "tb_role_user_selected" };

// (Keep your known roles list)
const KNOWN_ROLES = new Set([
    "Nominator",
    "Employee Supplier Admin",
    "Employee Sponsor",
    "DTV OPS Admin",
    "CP Channel Delegate",
    "FTE Nominator",
    "Read Only",
]);

function normalizeRole(raw) {
    if (!raw) return "";
    const x = String(raw).toLowerCase().replace(/\s+/g, " ").trim();
    if (x === "read only" || x === "readonly" || (x.includes("read") && x.includes("only"))) return "Read Only";
    if (x.includes("dtv ops admin") || x.includes("ops admin")) return "DTV OPS Admin";
    if (x.includes("employee supplier admin") || x.includes("supplier admin")) return "Employee Supplier Admin";
    if (x.includes("employee sponsor")) return "Employee Sponsor";
    if ((x.includes("fte") && x.includes("nominator")) || x === "fte nominator") return "FTE Nominator";
    if (x.includes("cp channel delegate") || (x.includes("cp") && x.includes("delegate")) || x === "cp delegate")
        return "CP Channel Delegate";
    if (x.includes("nominator")) return "Nominator";
    return raw;
}

export default function RoleGuard({ allowed = [], children }) {
    const nav = useNavigate();
    const loc = useLocation();
    const { accounts, instance } = useMsal();

    const isAuthed = !!(accounts?.[0]);

    // Track role read from sessionStorage
    const [role, setRole] = useState(() => normalizeRole(SS.getItem(K.role) || ""));
    const [roleRaw, setRoleRaw] = useState(() => SS.getItem(K.roleRaw) || "");

    // --- Timing windows
    const bootAtRef = useRef(Date.now());
    const GRACE_MS = 1000;   // small grace for RequireAuth to persist role
    const HARD_MS = 6000;    // after this, if authed and still no role, trigger a one-time re-login

    // Ticker so time-based guards progress
    const [, setTick] = useState(0);
    useEffect(() => {
        const iv = setInterval(() => setTick(t => t + 1), 250);
        return () => clearInterval(iv);
    }, []);
    const elapsed = Date.now() - bootAtRef.current;

    // IMPORTANT CHANGE:
    // If we already have a role, do NOT apply grace. That prevents the “Authorizing…” flash on tab switches.
    const withinGrace = role ? false : elapsed < GRACE_MS;
    const pastHard = elapsed >= HARD_MS;

    // Keep role in sync with sessionStorage writes.
    //
    // Two sources of change:
    //   (a) Cross-tab: window "storage" event fires when another tab writes sessionStorage.
    //   (b) Same-tab:  RoleSwitcher calls window.location.replace("/") which causes a full
    //       reload so the new value is picked up on mount — BUT route switches within the
    //       same SPA do NOT reload, so we need the poll to catch any same-tab updates too.
    //
    // IMPORTANT: the poll must NOT stop once a role is found.  If the user switches roles
    // the poll is the only mechanism that picks up the updated tb_role in the same tab.
    useEffect(() => {
        const readNow = () => {
            const next = normalizeRole(SS.getItem(K.role) || "");
            if (next) {
                if (next !== role) setRole(next);
            } else if (!isAuthed && role) {
                setRole("");
            }
            const rr = SS.getItem(K.roleRaw) || "";
            if (rr !== roleRaw) setRoleRaw(rr);
        };
        readNow();

        // Cross-tab storage events
        const onStorage = (e) => {
            if (e.storageArea !== sessionStorage) return;
            if (e.key === K.role || e.key === K.roleRaw || e.key === K.roleSelected) readNow();
        };
        window.addEventListener("storage", onStorage);

        // Continuous poll — runs forever while the guard is mounted so role switches
        // within the same SPA session are always reflected immediately.
        // 200 ms is imperceptible to users and cheap on CPU.
        const poll = setInterval(readNow, 200);

        return () => {
            window.removeEventListener("storage", onStorage);
            clearInterval(poll);
        };
        // role/roleRaw intentionally omitted — we want a stable interval, not one that
        // tears down and re-creates every time the role string changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts?.[0]]);

    // --- One-time re-login if authed but role never arrived
    const relogKey = "tb_role_relogin_once";
    const reloggingRef = useRef(false);
    useEffect(() => {
        if (!isAuthed) return;
        if (role) return;                // role already present
        if (!pastHard) return;           // wait until HARD_MS passes

        // prevent loops (only once per page load AND per session storage flag)
        if (reloggingRef.current) return;
        if (SS.getItem(relogKey) === "1") return;

        reloggingRef.current = true;
        SS.setItem(relogKey, "1");

        try {
            // Clear stale role/lookup so next pass is clean (optional but helpful)
            SS.removeItem(K.role);
            SS.removeItem(K.roleRaw);
            SS.removeItem("tb_lookup");
            SS.removeItem("tb_lookup_at");
            SS.removeItem("tb_lookup_ver");

            const href = (window.location?.href || "/").split("#")[0];
            const req = { ...getLoginRequest(), redirectStartPage: href };
            instance.loginRedirect(req);
        } catch {
            // If redirect cannot start, allow guard to continue and eventually go unauthorized
        }
    }, [isAuthed, role, pastHard, instance]);

    // Compute authorization with the role we have (do NOT default/guess)
    const allowedHere = useMemo(() => {
        const r = normalizeRole(role);
        if (!r || !KNOWN_ROLES.has(r)) return false;
        if (!allowed || allowed.length === 0 || allowed.includes("*")) return true;
        const normalizedAllowed = new Set(allowed.map(normalizeRole));
        return normalizedAllowed.has(r);
    }, [role, allowed]);

    // Defer redirect while within grace or while we wait for role (pre-timeout or while re-login about to start)
    useEffect(() => {
        const waitingForRole = isAuthed && !role && !pastHard;
        if (withinGrace || waitingForRole) return;

        // After HARD_MS, we either kicked a re-login (page navigates), or—if that fails—fall through:
        if (!role && SS.getItem(relogKey) === "1") {
            // We attempted re-login; render a neutral shell while navigation happens.
            return;
        }

        // If we’re here with no role and no re-login navigation occurred, deny.
        if (!allowedHere && window.location.pathname !== "/unauthorized") {
            nav("/unauthorized", { replace: true, state: { role, roleRaw, allowed, from: loc?.pathname } });
        }
    }, [withinGrace, pastHard, isAuthed, role, roleRaw, allowedHere, allowed, loc?.pathname, nav]);

    // RENDER:
    // IMPORTANT CHANGE:
    // - If 'waiting' AND we already have a role, do NOT show loader; just render children (prevents flicker).
    // - Otherwise, keep the original loader behavior.
    const relogIssued = SS.getItem(relogKey) === "1" && !role;

    const waiting =
        (!role && (withinGrace || (isAuthed && !pastHard))) ||
        (!isAuthed) ||
        relogIssued;

    if (waiting) {
        if (role) return children; // no flicker when role already exists
        return (
            <div className="main">
                <div className="card">{relogIssued ? "Re-authenticating…" : "Authorizing…"}</div>
            </div>
        );
    }

    if (!allowedHere) return null;
    return children;
}
